import React from 'react'
import { resolveApiAssetUrl } from '../../lib/api'
import {
  CARD_NEWS_SOURCE_MONTHLY_BRIEF,
  CARD_NEWS_SOURCE_OPTIONS,
  CARD_NEWS_SOURCE_TEMPLATE,
  extractCardNewsEmbedFromDbTemplatePreset,
  mergeMonthlyIssueContent,
} from '../../lib/monthlyIssueLanding'
import PublicPromoComplianceNotice from '../../ui/PublicPromoComplianceNotice'
import { getDefaultPersonalTemplateCode, getPersonalSectionMaxCount, getPersonalSectionMeta } from '../personalLanding/registry'
import {
  LANDING_LAYOUT_PRESETS,
  LANDING_SECTION_OPTIONS,
  createLandingSectionDraft,
  createLandingSectionsFromPreset,
  moveSection,
} from './registry'

/**
 * 개인 소개페이지 섹션 — 아코디언 목록 + 펼침 시 필드 편집.
 * @param {'full'|'preset'} mode — preset: 운영 템플릿용(파일 업로드 숨김)
 */
export default function PersonalLandingSectionsEditor({
  mode = 'full',
  landingSections,
  setLandingSections,
  expandedSectionId,
  setExpandedSectionId,
  /** (index, updater) => void */
  updateSectionContent,
  /** full 모드 전용 컨텍스트 */
  full = null,
  /** 로컬 프리셋 적용 시 기준 데이터(소개문 등) */
  presetData = {},
  /** 운영 DB 템플릿 { id, code, name, preset }[] — 있으면 이것만 노출, 없으면 로컬 프리셋만 노출 */
  dbTemplates = [],
  onApplyDbTemplate,
  /** 개인홈 편집: 섹션 구성 → 순서 → 내용 단계 흐름 */
  guidedFlow = false,
  guidedStartPhase = 1,
  guidedStartIndex = 0,
  guidedStartKey = 0,
}) {
  const isFull = mode === 'full' && full
  const [draggingIndex, setDraggingIndex] = React.useState(null)
  const [dragOverIndex, setDragOverIndex] = React.useState(null)
  const [templateApplyId, setTemplateApplyId] = React.useState('')
  const [guidedPhase, setGuidedPhase] = React.useState(1)
  const [guidedEditIndex, setGuidedEditIndex] = React.useState(0)
  const [textareaDrafts, setTextareaDrafts] = React.useState({})

  const hasDbTemplates = Array.isArray(dbTemplates) && dbTemplates.length > 0 && typeof onApplyDbTemplate === 'function'

  React.useEffect(() => {
    if (!guidedFlow) return
    setGuidedEditIndex((idx) => {
      const max = Math.max(0, landingSections.length - 1)
      return Math.min(Math.max(0, idx), max)
    })
  }, [guidedFlow, landingSections.length])

  React.useEffect(() => {
    if (!guidedFlow) return
    const nextPhase = Math.min(3, Math.max(1, Number(guidedStartPhase) || 1))
    const maxIndex = Math.max(0, landingSections.length - 1)
    setGuidedPhase(nextPhase)
    setGuidedEditIndex(Math.min(Math.max(0, Number(guidedStartIndex) || 0), maxIndex))
  }, [guidedFlow, guidedStartIndex, guidedStartKey, guidedStartPhase, landingSections.length])

  /** 1=구성(추가·삭제·메뉴·템플릿), 2=순서만, 3=내용만 */
  const phaseAllowsReorder = !guidedFlow || guidedPhase === 2
  const phaseAllowsTemplates = !guidedFlow || guidedPhase === 1
  const phaseAllowsAdd = !guidedFlow || guidedPhase === 1
  const phaseAllowsSectionRemove = !guidedFlow || guidedPhase === 1
  const phaseAllowsMenuToggle = !guidedFlow || guidedPhase === 1

  const availableSections = LANDING_SECTION_OPTIONS.filter((item) => {
    const max = getPersonalSectionMaxCount(item.type)
    const n = landingSections.filter((section) => section.type === item.type).length
    return n < max
  })

  const toggleExpanded = (id) => {
    setExpandedSectionId((prev) => (prev === id ? '' : id))
  }

  const getTextareaDraftKey = (section, index, field) => `${section?.id || `${section?.type || 'section'}-${index}`}:${field}`
  const getTextareaDraft = (key, fallback) => (
    Object.prototype.hasOwnProperty.call(textareaDrafts, key) ? textareaDrafts[key] : fallback
  )
  const setTextareaDraft = (key, value) => {
    setTextareaDrafts((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }))
  }

  const renderSectionBody = (section, index) => {
    const selectedSection = section
    const selectedSectionIndex = index

    return (
      <div className="space-y-4 border-t border-slate-100 bg-white px-4 py-4">
        {(() => {
          const meta = getPersonalSectionMeta(selectedSection.type)
          if (!meta?.templates || meta.templates.length < 2) return null
          return (
            <div>
              <label className="block text-xs text-slate-600">레이아웃</label>
              <select
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={selectedSection.templateCode || getDefaultPersonalTemplateCode(selectedSection.type)}
                onChange={(e) => {
                  const code = e.target.value
                  setLandingSections((prev) =>
                    prev.map((s, i) => (i === selectedSectionIndex ? { ...s, templateCode: code } : s))
                  )
                }}
              >
                {meta.templates.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )
        })()}

        {['about-profile', 'media-gallery', 'free-text', 'cta', 'ad-review'].includes(selectedSection.type) && (
          <>
            <div>
              <label className="block text-xs text-slate-600">섹션 제목</label>
              <input
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={selectedSection?.content?.title || ''}
                onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, title: e.target.value }))}
                maxLength={120}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600">
                {selectedSection.type === 'ad-review' ? '심의필·법적 표기 문구' : '설명'}
              </label>
              <textarea
                className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[96px]"
                value={selectedSection?.content?.body || ''}
                onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, body: e.target.value }))}
                maxLength={selectedSection.type === 'ad-review' ? 900 : 600}
              />
              {selectedSection.type === 'ad-review' ? (
                <div className="mt-2 space-y-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-xs leading-relaxed text-amber-950">
                  <p className="font-semibold text-amber-950">광고·준법·심의필(필수 표기)</p>
                  <p className="text-amber-950/95">
                    금융상품 광고·업무광고에 해당하는 공개 페이지라면, 소속사에서 안내하는{' '}
                    <span className="font-semibold">준법감시인 확인·광고 심의</span>를 마친 뒤 받은{' '}
                    <span className="font-semibold">심의필 번호·유효기간·문구</span>를 여기에 그대로 적어 주세요.
                  </p>
                  <p className="text-[11px] text-amber-900/90">
                    이 블록은 <span className="font-semibold">페이지 하단에 항상 노출</span>되며, 방문자가 심의 정보를 볼 수 있게 하는 용도입니다. 상단 메뉴(앵커)에는 포함되지 않습니다.
                  </p>
                </div>
              ) : null}
            </div>
          </>
        )}

        {['proof-stats', 'customer-case'].includes(selectedSection.type) && (
          <>
            {(() => {
              const draftKey = getTextareaDraftKey(selectedSection, selectedSectionIndex, 'items')
              const serializedItems = Array.isArray(selectedSection?.content?.items)
                ? selectedSection.content.items.map((item) => `${item?.label || item?.title || ''}:${item?.value || item?.body || ''}`).join('\n')
                : ''
              return (
                <>
                  <div>
                    <label className="block text-xs text-slate-600">섹션 제목</label>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={selectedSection?.content?.title || ''}
                      onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, title: e.target.value }))}
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600">
                      {selectedSection.type === 'proof-stats' ? '지표 (라벨:값, 줄바꿈 구분)' : '사례 (제목:설명, 줄바꿈 구분)'}
                    </label>
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[120px]"
                      value={getTextareaDraft(draftKey, serializedItems)}
                      onChange={(e) => {
                        const raw = e.target.value
                        setTextareaDraft(draftKey, raw)
                        const rows = String(raw || '')
                          .split('\n')
                          .map((line) => line.trim())
                          .filter(Boolean)
                          .slice(0, 8)
                          .map((line) => {
                            const [left, ...right] = line.split(':')
                            const rightJoined = right.join(':').trim()
                            if (selectedSection.type === 'proof-stats') return { label: (left || '').trim(), value: rightJoined }
                            return { title: (left || '').trim(), body: rightJoined }
                          })
                        updateSectionContent(selectedSectionIndex, (content) => ({ ...content, items: rows }))
                      }}
                    />
                  </div>
                </>
              )
            })()}
          </>
        )}

        {selectedSection.type === 'monthly-issue' && (() => {
          const mc = mergeMonthlyIssueContent(selectedSection.content)
          const toast = (msg, kind = 'error') => {
            try { window?.toast?.show?.(msg, kind) } catch { /* noop */ }
          }
          const applyTemplateEmbed = () => {
            const t = (dbTemplates || []).find((x) => String(x.id) === String(templateApplyId))
            if (!t) {
              toast('템플릿을 선택해 주세요.', 'error')
              return
            }
            const preset = t.preset && typeof t.preset === 'object' ? t.preset : {}
            const embed = extractCardNewsEmbedFromDbTemplatePreset(preset)
            if (!embed) {
              toast('선택한 템플릿에 카드뉴스 스냅샷이 없습니다. 운영에서 템플릿 preset의「이달의 이슈」에 cardNewsTemplateEmbed를 넣어 주세요.', 'error')
              return
            }
            updateSectionContent(selectedSectionIndex, (c) => ({
              ...mergeMonthlyIssueContent(c),
              cardNewsSource: CARD_NEWS_SOURCE_TEMPLATE,
              cardNewsTemplateId: t.id,
              cardNewsTemplateEmbed: {
                periodLabel: embed.periodLabel || null,
                payload: embed.payload || null,
                images: embed.images,
              },
            }))
            toast('템플릿 카드뉴스를 이 섹션에 적용했습니다. 저장하면 공개 페이지에 반영됩니다.', 'success')
          }
          return (
            <div className="space-y-4 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
              <div className="text-xs font-semibold text-slate-700">카드뉴스 소스</div>
              <div className="flex flex-col gap-2">
                {CARD_NEWS_SOURCE_OPTIONS.map((opt) => (
                  <label key={opt.id} className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                    <input
                      type="radio"
                      name={`card-news-source-${selectedSection.id}`}
                      className="mt-0.5"
                      checked={mc.cardNewsSource === opt.id}
                      onChange={() => updateSectionContent(selectedSectionIndex, (c) => ({
                        ...mergeMonthlyIssueContent(c),
                        cardNewsSource: opt.id,
                      }))}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>

              {mc.cardNewsSource === CARD_NEWS_SOURCE_MONTHLY_BRIEF && (
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-[11px] leading-relaxed text-slate-600">
                    공개 페이지에는 <span className="font-semibold text-slate-800">게시된 월간 카드뉴스 중 가장 최근 호</span>가 자동으로 연결되며, 그 호에 등록된 카드는 <span className="font-semibold text-slate-800">전부</span> 노출됩니다.
                  </p>
                </div>
              )}

              {mc.cardNewsSource === CARD_NEWS_SOURCE_TEMPLATE && hasDbTemplates && (
                <div className="space-y-2 border-t border-slate-200 pt-3">
                  <label className="block text-xs text-slate-600">운영 템플릿</label>
                  <select
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={templateApplyId}
                    onChange={(e) => setTemplateApplyId(e.target.value)}
                  >
                    <option value="">선택…</option>
                    {dbTemplates.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.name || t.code}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rounded-lg bg-brand-navy px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
                    onClick={applyTemplateEmbed}
                  >
                    템플릿에 담긴 카드뉴스 적용
                  </button>
                  <p className="text-[10px] leading-snug text-slate-500">
                    템플릿 preset의「이달의 이슈」섹션에 <span className="font-mono text-[10px]">cardNewsTemplateEmbed</span>가 있어야 합니다. 운영 화면(개인홈 템플릿)에서 저장 시 포함할 수 있습니다.
                  </p>
                </div>
              )}

              {mc.cardNewsSource === CARD_NEWS_SOURCE_TEMPLATE && !hasDbTemplates && (
                <p className="text-[11px] text-amber-800">등록된 운영 템플릿이 없습니다. 월간 이슈(보험사 소식지 기반)만 사용할 수 있습니다.</p>
              )}
            </div>
          )
        })()}

        {selectedSection.type === 'service-tags' && (
          <>
            <div>
              <label className="block text-xs text-slate-600">섹션 제목</label>
              <input
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={selectedSection?.content?.title || ''}
                onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, title: e.target.value }))}
                maxLength={120}
              />
            </div>
            {selectedSection.templateCode === 'personal-services.cards-v1' && (
              (() => {
                const draftKey = getTextareaDraftKey(selectedSection, selectedSectionIndex, 'service-items')
                const serializedItems = Array.isArray(selectedSection?.content?.items)
                  ? selectedSection.content.items.map((item) => `${item?.title || ''}:${item?.body || ''}`).join('\n')
                  : ''
                return (
                  <div>
                    <label className="block text-xs text-slate-600">서비스 카드 (제목:한 줄 설명, 줄바꿈 구분)</label>
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[120px]"
                      value={getTextareaDraft(draftKey, serializedItems)}
                      onChange={(e) => {
                        const raw = e.target.value
                        setTextareaDraft(draftKey, raw)
                        const rows = String(raw || '')
                          .split('\n')
                          .map((line) => line.trim())
                          .filter(Boolean)
                          .slice(0, 8)
                          .map((line) => {
                            const [left, ...right] = line.split(':')
                            return { title: (left || '').trim(), body: right.join(':').trim() }
                          })
                        updateSectionContent(selectedSectionIndex, (content) => ({ ...content, items: rows }))
                      }}
                    />
                  </div>
                )
              })()
            )}
          </>
        )}

        {['credentials', 'values'].includes(selectedSection.type) && (
          <>
            <div>
              <label className="block text-xs text-slate-600">섹션 제목</label>
              <input
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={selectedSection?.content?.title || ''}
                onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, title: e.target.value }))}
                maxLength={120}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600">
                {selectedSection.type === 'credentials'
                  ? '항목 (제목|부제|연도, 줄바꿈 구분)'
                  : '카드 (제목:본문, 줄바꿈 구분)'}
              </label>
              {(() => {
                const draftKey = getTextareaDraftKey(selectedSection, selectedSectionIndex, 'credential-value-items')
                const serializedItems = Array.isArray(selectedSection?.content?.items)
                  ? selectedSection.type === 'credentials'
                    ? selectedSection.content.items
                      .map((item) => [item?.title || '', item?.subtitle || '', item?.year || ''].join('|'))
                      .join('\n')
                    : selectedSection.content.items.map((item) => `${item?.title || ''}:${item?.body || ''}`).join('\n')
                  : ''
                return (
                  <textarea
                    className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[120px]"
                    value={getTextareaDraft(draftKey, serializedItems)}
                    onChange={(e) => {
                      const raw = e.target.value
                      setTextareaDraft(draftKey, raw)
                      const rows = String(raw || '')
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean)
                        .slice(0, 12)
                      if (selectedSection.type === 'credentials') {
                        const items = rows.map((line) => {
                          const [a, b, c] = line.split('|').map((s) => s.trim())
                          return { title: a || '', subtitle: b || '', year: c || '' }
                        })
                        updateSectionContent(selectedSectionIndex, (content) => ({ ...content, items }))
                      } else {
                        const items = rows.map((line) => {
                          const [left, ...right] = line.split(':')
                          return { title: (left || '').trim(), body: right.join(':').trim() }
                        })
                        updateSectionContent(selectedSectionIndex, (content) => ({ ...content, items }))
                      }
                    }}
                  />
                )
              })()}
            </div>
          </>
        )}

        {selectedSection.type === 'faq' && (
          <>
            <div>
              <label className="block text-xs text-slate-600">섹션 제목</label>
              <input
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={selectedSection?.content?.title || ''}
                onChange={(e) => updateSectionContent(selectedSectionIndex, (content) => ({ ...content, title: e.target.value }))}
                maxLength={120}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600">질문:답변 (줄마다 한 쌍, 첫 : 기준 분리)</label>
              {(() => {
                const draftKey = getTextareaDraftKey(selectedSection, selectedSectionIndex, 'faq-items')
                const serializedItems = Array.isArray(selectedSection?.content?.items)
                  ? selectedSection.content.items.map((item) => `${item?.q || ''}:${item?.a || ''}`).join('\n')
                  : ''
                return (
                  <textarea
                    className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[140px]"
                    value={getTextareaDraft(draftKey, serializedItems)}
                    onChange={(e) => {
                      const raw = e.target.value
                      setTextareaDraft(draftKey, raw)
                      const rows = String(raw || '')
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean)
                        .slice(0, 16)
                        .map((line) => {
                          const idx = line.indexOf(':')
                          if (idx < 0) return { q: line, a: '' }
                          return { q: line.slice(0, idx).trim(), a: line.slice(idx + 1).trim() }
                        })
                      updateSectionContent(selectedSectionIndex, (content) => ({ ...content, items: rows }))
                    }}
                  />
                )
              })()}
            </div>
          </>
        )}

        {selectedSection.type === 'hero' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 space-y-3">
              <div className="text-xs font-semibold text-slate-600">히어로 문구 (선택)</div>
              {/* <p className="text-[11px] text-slate-500">
                비워 두면 공개 페이지에서 이름·직함·한 줄 소개·간단 소개글 순으로 자동 표시됩니다. 입력하면 해당 필드가 우선합니다. 엔터로 줄바꿈할 수 있습니다.
                카카오 등 링크 공유 시 미리보기 제목(OG)은 메인 헤드라인을 쓴 경우 그 문구가 우선하고, 비어 있으면 한 줄 소개 → 이름·직함 → 이름 + 입니다. 순입니다.
              </p> */}
              <div>
                <label className="block text-sm text-slate-600">상단 라벨</label>
                <textarea
                  className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[48px]"
                  value={selectedSection?.content?.eyebrow || ''}
                  onChange={(e) => updateSectionContent(selectedSectionIndex, (c) => ({ ...c, eyebrow: e.target.value }))}
                  maxLength={120}
                  rows={2}
                  placeholder="예) 맞춤 보장 · 1:1 설계"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600">메인 헤드라인</label>
                <textarea
                  className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[64px]"
                  value={selectedSection?.content?.title || ''}
                  onChange={(e) => updateSectionContent(selectedSectionIndex, (c) => ({ ...c, title: e.target.value }))}
                  maxLength={200}
                  rows={3}
                  placeholder="비워두면 이름과 직함이 표시됩니다"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600">부가 설명</label>
                <textarea
                  className="mt-1 w-full rounded border px-3 py-2 text-sm min-h-[72px]"
                  value={selectedSection?.content?.subtitle || ''}
                  onChange={(e) => updateSectionContent(selectedSectionIndex, (c) => ({ ...c, subtitle: e.target.value }))}
                  maxLength={400}
                  rows={4}
                  placeholder="비워두면 한 줄 소개·간단 소개글이 이어집니다"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600">메인 버튼 문구</label>
                <input
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={selectedSection?.content?.ctaText || ''}
                  onChange={(e) => updateSectionContent(selectedSectionIndex, (c) => ({ ...c, ctaText: e.target.value }))}
                  maxLength={80}
                  placeholder="예) 상담 문의하기"
                />
              </div>
            </div>
            <div className="text-xs font-semibold text-slate-600">상단 소개(히어로)에 쓰이는 정보</div>
            {!isFull ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                프로필 사진·회사명·SNS 등은 사용자가 개인홈페이지 편집 화면에서 설정합니다.
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm text-slate-600">프로필 사진</label>
                  <div className="mt-0.5 text-[11px] text-slate-500">권장 정사각형 800×800px 이상.</div>
                  <div className="mt-1 flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      onChange={(e) => {
                        const f = e?.target?.files?.[0]
                        if (!f) return
                        full.uploadProfileImageMutation.mutate(f)
                        try { e.target.value = '' } catch { }
                      }}
                      disabled={full.uploadProfileImageMutation.isLoading || full.updateMutation.isLoading}
                    />
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[12px] text-slate-600">
                        {full.profileImageFileId ? <>현재: <span className="font-mono">{full.profileImageFileId}</span></> : <>현재: 없음</>}
                      </div>
                      <button
                        type="button"
                        className={`rounded border px-3 py-2 text-sm ${full.profileImageFileId ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'}`}
                        onClick={() => {
                          if (!full.profileImageFileId) return
                          full.setProfileImageFileId('')
                          full.updateMutation.mutate({ __keepOpen: true, profileImageFileId: '' })
                        }}
                        disabled={!full.profileImageFileId || full.updateMutation.isLoading}
                      >
                        이미지 삭제
                      </button>
                    </div>
                    {full.profileImageUrl && (
                      <div className="flex items-center gap-2">
                        <img
                          src={resolveApiAssetUrl(full.profileImageUrl)}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover object-center border"
                          decoding="async"
                          loading="lazy"
                          onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }}
                        />
                        <span className="text-[11px] text-slate-500">미리보기</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600">회사명(자동 매핑, 필요 시 수정)</label>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2"
                      value={full.companyNameOverride}
                      onChange={(e) => full.setCompanyNameOverride(e.target.value)}
                      maxLength={120}
                      placeholder="비워두면 자동 표시"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600">직책명(비어있으면 팀장)</label>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2"
                      value={full.jobTitle}
                      onChange={(e) => full.setJobTitle(e.target.value)}
                      maxLength={40}
                      placeholder="예) 팀장"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600">한 줄 소개(선택)</label>
                  <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[48px] text-sm" value={full.bioShort} onChange={(e) => full.setBioShort(e.target.value)} maxLength={120} rows={2} placeholder="엔터로 두 줄까지 나눌 수 있습니다" />
                  {/* <p className="mt-1 text-[11px] text-slate-500">히어로 메인 헤드라인이 비어 있을 때 링크 미리보기 제목으로 쓰입니다.</p> */}
                </div>
                <div>
                  <label className="block text-sm text-slate-600">간단한 소개글(선택)</label>
                  <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[72px]" value={full.introText} onChange={(e) => full.setIntroText(e.target.value)} maxLength={300} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600">유튜브 링크(선택)</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={full.youtubeUrl} onChange={(e) => full.setYoutubeUrl(e.target.value)} maxLength={300} placeholder="https://youtube.com/..." />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600">인스타그램 링크(선택)</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={full.instagramUrl} onChange={(e) => full.setInstagramUrl(e.target.value)} maxLength={300} placeholder="https://instagram.com/..." />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600">블로그 링크(선택)</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={full.blogUrl} onChange={(e) => full.setBlogUrl(e.target.value)} maxLength={300} placeholder="https://..." />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {selectedSection.type === 'service-tags' && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-600">태그 내용</div>
            {!isFull ? (
              <div className="text-xs text-slate-500">사용자가 서비스 태그를 입력합니다.</div>
            ) : (
              <div>
                <label className="block text-sm text-slate-600">서비스 태그(#로 구분, 선택)</label>
                <input
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={full.serviceTags}
                  onChange={(e) => full.setServiceTags(e.target.value)}
                  maxLength={300}
                  placeholder="예) #보장점검#실손정리#재무상담"
                />
              </div>
            )}
          </div>
        )}

        {selectedSection.type === 'media-gallery' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold">소개페이지 메인 사진(슬라이드)</div>
              {isFull ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span>사용</span>
                  <input
                    type="checkbox"
                    checked={full.landingGalleryEnabled}
                    onChange={(e) => {
                      const next = e.target.checked
                      full.setLandingGalleryEnabled(next)
                      full.updateMutation.mutate({ __keepOpen: true, landingGalleryEnabled: next })
                    }}
                  />
                </label>
              ) : null}
            </div>
            <div className="text-[12px] text-slate-600">
              권장 가로 1200px 이상(4:3 또는 16:9). 세로가 큰 이미지는 위·아래가 잘릴 수 있습니다.
            </div>
            {!isFull ? (
              <div className="text-xs text-slate-500">이미지는 사용자가 업로드합니다.</div>
            ) : (
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                  onChange={(e) => {
                    const files = Array.from(e?.target?.files || [])
                    if (files.length === 0) return
                    const remain = Math.max(0, 5 - (Array.isArray(full.landingGalleryItems) ? full.landingGalleryItems.length : 0))
                    const picked = files.slice(0, remain)
                    if (picked.length > 0) full.uploadLandingGalleryMutation.mutate(picked)
                    try { e.target.value = '' } catch { }
                  }}
                  disabled={full.uploadLandingGalleryMutation.isLoading || full.updateMutation.isLoading}
                />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[12px] text-slate-600">
                    현재 {Array.isArray(full.landingGalleryItems) ? full.landingGalleryItems.length : 0}장 / 5장
                  </div>
                  <button
                    type="button"
                    className={`rounded border px-3 py-2 text-sm ${(Array.isArray(full.landingGalleryItems) && full.landingGalleryItems.length > 0) ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'}`}
                    onClick={() => {
                      if (!Array.isArray(full.landingGalleryItems) || full.landingGalleryItems.length === 0) return
                      full.setLandingGalleryItems([])
                      full.setLandingGalleryEnabled(false)
                      full.updateMutation.mutate({ __keepOpen: true, landingGalleryEnabled: false, landingGalleryFileIds: [] })
                    }}
                    disabled={!Array.isArray(full.landingGalleryItems) || full.landingGalleryItems.length === 0 || full.updateMutation.isLoading}
                  >
                    전체 삭제
                  </button>
                </div>
                {Array.isArray(full.landingGalleryItems) && full.landingGalleryItems.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {full.landingGalleryItems.map((it, idx) => (
                      <div key={`${it?.fileId || 'x'}-${idx}`} className="relative rounded-lg border overflow-hidden bg-slate-50">
                        <img
                          src={resolveApiAssetUrl(it?.url)}
                          alt=""
                          className="w-full h-24 object-contain object-center p-2"
                          decoding="async"
                          loading="lazy"
                          onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }}
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 rounded bg-white/90 border px-2 py-1 text-xs hover:bg-white"
                          onClick={() => {
                            const next = full.landingGalleryItems.filter((_, i) => i !== idx)
                            full.setLandingGalleryItems(next)
                            const ids = next.map((x) => x?.fileId).filter(Boolean)
                            full.updateMutation.mutate({ __keepOpen: true, landingGalleryEnabled: ids.length > 0 ? full.landingGalleryEnabled : false, landingGalleryFileIds: ids })
                          }}
                          disabled={full.updateMutation.isLoading}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {selectedSection.type === 'image-card' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold">이미지 카드(단일 이미지)</div>
              {isFull ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span>표시</span>
                  <input type="checkbox" checked={full.imageCardEnabled} onChange={(e) => full.setImageCardEnabled(e.target.checked)} />
                </label>
              ) : null}
            </div>
            {!isFull ? (
              <div className="text-xs text-slate-500">이미지는 사용자가 업로드합니다.</div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      full.setImageCardEnabled(true)
                      full.setShowCareerCard(false)
                      full.setShowActivityAreaCard(false)
                      full.setShowFreeTextCard(false)
                    }}
                  >
                    이미지 카드만 사용
                  </button>
                  {/* <span className="text-[12px] text-slate-500">텍스트 카드는 각 섹션에서 표시 여부를 끌 수 있습니다.</span> */}
                </div>
                <div className="text-[11px] text-slate-500">권장 1200×675px(16:9) 또는 1200×800px(3:2) 이상, 가로형이면 잘 맞습니다.</div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    onChange={(e) => {
                      const f = e?.target?.files?.[0]
                      if (!f) return
                      full.uploadImageCardMutation.mutate(f)
                      try { e.target.value = '' } catch { }
                    }}
                    disabled={full.uploadImageCardMutation.isLoading || full.updateMutation.isLoading}
                  />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[12px] text-slate-600">
                      {full.imageCardFileId ? <>현재: <span className="font-mono">{full.imageCardFileId}</span></> : <>현재: 없음</>}
                    </div>
                    <button
                      type="button"
                      className={`rounded border px-3 py-2 text-sm ${full.imageCardFileId ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'}`}
                      onClick={() => {
                        if (!full.imageCardFileId) return
                        full.setImageCardFileId('')
                        full.setImageCardUrl('')
                        full.updateMutation.mutate({ imageCardFileId: '' })
                      }}
                      disabled={!full.imageCardFileId || full.updateMutation.isLoading}
                    >
                      이미지 삭제
                    </button>
                  </div>
                  {full.imageCardUrl && (
                    <div className="rounded-lg border overflow-hidden bg-slate-50">
                      <img src={resolveApiAssetUrl(full.imageCardUrl)} alt="" className="w-full object-contain object-center" decoding="async" loading="lazy" onError={(e) => { try { e.currentTarget.style.display = 'none' } catch { } }} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {selectedSection.type === 'career' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-slate-600">경력 내용</div>
              {isFull ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span>표시</span>
                  <input type="checkbox" checked={full.showCareerCard} onChange={(e) => full.setShowCareerCard(e.target.checked)} />
                </label>
              ) : null}
            </div>
            {isFull ? (
              <textarea className="w-full rounded border px-3 py-2 min-h-[72px] text-sm" value={full.careerText} onChange={(e) => full.setCareerText(e.target.value)} maxLength={500} placeholder="예) 2018~2022 ○○보험 / 2023~ 현재 ○○GA" />
            ) : (
              <div className="text-xs text-slate-500">사용자가 경력 텍스트를 입력합니다.</div>
            )}
          </div>
        )}

        {selectedSection.type === 'activity' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-slate-600">활동지역</div>
              {isFull ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span>표시</span>
                  <input type="checkbox" checked={full.showActivityAreaCard} onChange={(e) => full.setShowActivityAreaCard(e.target.checked)} />
                </label>
              ) : null}
            </div>
            {isFull ? (
              <textarea className="w-full rounded border px-3 py-2 min-h-[72px] text-sm" value={full.activityAreaText} onChange={(e) => full.setActivityAreaText(e.target.value)} maxLength={200} />
            ) : (
              <div className="text-xs text-slate-500">사용자가 활동 지역을 입력합니다.</div>
            )}
          </div>
        )}

        {selectedSection.type === 'free-text' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-slate-600">자유 작성</div>
              {isFull ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <span>표시</span>
                  <input type="checkbox" checked={full.showFreeTextCard} onChange={(e) => full.setShowFreeTextCard(e.target.checked)} />
                </label>
              ) : null}
            </div>
            {isFull ? (
              <>
                <textarea className="w-full rounded border px-3 py-2 min-h-[120px] text-sm" value={full.freeText} onChange={(e) => full.setFreeText(e.target.value)} maxLength={2000} placeholder="설계사가 원하는 내용을 자유롭게 작성하는 영역입니다." />
                <div className="text-[11px] text-slate-500">텍스트로만 노출됩니다(줄바꿈 지원).</div>
              </>
            ) : (
              <div className="text-xs text-slate-500">사용자가 자유 소개를 입력합니다.</div>
            )}
          </div>
        )}

        {selectedSection.type === 'contact' && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-600">문의에 쓰이는 정보</div>
            {!isFull ? (
              <div className="text-xs text-slate-500">주소·카카오 링크 등은 사용자가 입력합니다.</div>
            ) : (
              <>
                <div>
                  <label className="block text-sm text-slate-600">주소(자동 매핑, 필요 시 수정)</label>
                  <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[64px]" value={full.addressText} onChange={(e) => full.setAddressText(e.target.value)} maxLength={200} placeholder="비워두면 자동 표시(있는 경우)" />
                </div>
                <div>
                  <label className="block text-sm text-slate-600">모바일 팩스 연락처(선택)</label>
                  <input className="mt-1 w-full rounded border px-3 py-2" value={full.mobileFaxNumber} onChange={(e) => full.setMobileFaxNumber(e.target.value)} maxLength={30} placeholder="예) 0504-123-4567" />
                </div>
                <div>
                  <label className="block text-sm text-slate-600">카카오 상담 링크(선택)</label>
                  <input className="mt-1 w-full rounded border px-3 py-2" value={full.kakaoChatUrl} onChange={(e) => full.setKakaoChatUrl(e.target.value)} maxLength={300} placeholder="https://open.kakao.com/..." />
                </div>
              </>
            )}
          </div>
        )}

        {!['about-profile', 'media-gallery', 'free-text', 'cta', 'ad-review', 'proof-stats', 'customer-case', 'monthly-issue', 'hero', 'service-tags', 'image-card', 'career', 'activity', 'contact', 'credentials', 'values', 'faq'].includes(selectedSection.type) && (
          <div className="text-xs text-slate-500">이 섹션은 문구를 조정하고, 연결 필드가 있는 섹션을 펼쳐 입력합니다.</div>
        )}
      </div>
    )
  }

  return (
    <div className={`rounded-2xl border border-slate-200 bg-slate-50/80 ${guidedFlow ? 'p-3' : 'p-4'}`}>
      {!guidedFlow ? <PublicPromoComplianceNotice density="compact" /> : null}
      {guidedFlow ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {[
            { id: 1, label: '섹션구성' },
            { id: 2, label: '순서' },
            { id: 3, label: '내용편집' },
          ].map((step) => (
            <button
              key={step.id}
              type="button"
              onClick={() => setGuidedPhase(step.id)}
              className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition ${guidedPhase === step.id
                ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                }`}
            >
              {step.id}/3 {step.label}
            </button>
          ))}
        </div>
      ) : null}
      {guidedFlow && guidedPhase === 1 ? (
        <p className="mb-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] leading-snug text-slate-600">
          이 단계에서는 <span className="font-semibold text-slate-800">섹션 추가·삭제</span>와 메뉴 표시, 레이아웃 템플릿만 다룹니다. 순서 변경·문구 입력은 다음 단계에서 해 주세요.
        </p>
      ) : null}
      {guidedFlow && guidedPhase === 2 ? (
        <p className="mb-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] leading-snug text-slate-600">
          이 단계에서는 <span className="font-semibold text-slate-800">순서만</span> 바꿉니다. 왼쪽 <span className="font-mono text-slate-600">⋮⋮</span> 드래그 또는 ↑↓ 버튼을 사용하세요.
        </p>
      ) : null}
      <div className="text-sm font-semibold text-slate-800">섹션 구성</div>
      {!guidedFlow ? (
        <div className="mt-1 text-xs text-slate-500">
          섹션을 눌러 펼치면 내용을 수정할 수 있습니다. 상단 메뉴(네비) 노출은 체크박스로 바꿉니다. 왼쪽 <span className="font-mono text-slate-600">⋮⋮</span> 핸들을 드래그하면 순서를 바꿀 수 있습니다.
        </div>
      ) : null}

      {guidedFlow && guidedPhase === 3 && landingSections.length > 0 ? (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600">편집할 섹션</label>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={guidedEditIndex}
              onChange={(e) => setGuidedEditIndex(Number(e.target.value))}
            >
              {landingSections.map((section, idx) => {
                const meta = LANDING_SECTION_OPTIONS.find((item) => item.type === section.type)
                const title = String(section?.content?.title || '').trim()
                return (
                  <option key={section.id || `${section.type}-${idx}`} value={idx}>
                    {meta?.label || section.type}{title ? ` — ${title}` : ''}
                  </option>
                )
              })}
            </select>
          </div>
          <div className="border-t border-slate-100 pt-4">
            {renderSectionBody(landingSections[guidedEditIndex], guidedEditIndex)}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              disabled={guidedEditIndex <= 0}
              onClick={() => setGuidedEditIndex((n) => Math.max(0, n - 1))}
            >
              이전 섹션
            </button>
            <span className="text-[11px] text-slate-500">
              {guidedEditIndex + 1} / {landingSections.length}
            </span>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              disabled={guidedEditIndex >= landingSections.length - 1}
              onClick={() => setGuidedEditIndex((n) => Math.min(landingSections.length - 1, n + 1))}
            >
              다음 섹션
            </button>
          </div>
        </div>
      ) : null}

      <div className={`mt-3 flex flex-wrap gap-1.5 ${phaseAllowsTemplates ? '' : 'hidden'}`}>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5 w-full">
          레이아웃 템플릿
        </span>
        {hasDbTemplates
          ? dbTemplates.map((t) => (
            <button
              key={t.id || t.code}
              type="button"
              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-50"
              onClick={() => onApplyDbTemplate(t)}
            >
              {t.name || t.code}
            </button>
          ))
          : LANDING_LAYOUT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setLandingSections(createLandingSectionsFromPreset(presetData, preset.id))}
              title={preset.description}
            >
              {preset.label}
            </button>
          ))}
      </div>

      <div className={`mt-4 space-y-2 ${guidedFlow && guidedPhase === 3 ? 'hidden' : ''}`}>
        {landingSections.map((section, index) => {
          const meta = LANDING_SECTION_OPTIONS.find((item) => item.type === section.type)
          const required = Boolean(meta?.required)
          const enabled = section.enabled !== false
          const expanded = expandedSectionId === section.id
          const isDragging = draggingIndex === index
          const isDropTarget = dragOverIndex === index && draggingIndex !== null && draggingIndex !== index
          const showBodyInCard = !guidedFlow && expanded
          const contentTitle = String(section?.content?.title || '').trim()
          const sectionTypeLabel = meta?.label || section.type
          const personalMeta = getPersonalSectionMeta(section.type)
          const activeTemplateCode = section.templateCode || getDefaultPersonalTemplateCode(section.type)
          const activeTemplateLabel = personalMeta?.templates?.find((item) => item.code === activeTemplateCode)?.label
          return (
            <div
              key={section.id || `${section.type}-${index}`}
              className={`rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-shadow ${isDragging ? 'opacity-50' : ''} ${isDropTarget ? 'ring-2 ring-brand-navy/35 border-brand-navy/30' : ''}`}
              onDragOver={(e) => {
                if (!phaseAllowsReorder || draggingIndex === null) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                setDragOverIndex(index)
              }}
              onDrop={(e) => {
                if (!phaseAllowsReorder) return
                e.preventDefault()
                const raw = e.dataTransfer.getData('application/x-sgez-section-index')
                const from = Number(raw)
                setDragOverIndex(null)
                setDraggingIndex(null)
                if (Number.isNaN(from) || from === index) return
                setLandingSections(moveSection(landingSections, from, index))
              }}
            >
              <div className={`grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 ${expanded ? 'bg-slate-50/90 border-b border-slate-100' : ''}`}>
                <span
                  draggable={phaseAllowsReorder}
                  role="button"
                  tabIndex={0}
                  aria-label={`${meta?.label || section.type} 섹션 순서 변경(드래그)`}
                  title={phaseAllowsReorder ? '드래그하여 순서 변경' : '「순서」단계에서 바꿀 수 있습니다'}
                  className={`select-none self-start rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold leading-none text-slate-500 ${phaseAllowsReorder
                    ? 'cursor-grab hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600 active:cursor-grabbing'
                    : 'cursor-not-allowed opacity-40'
                    }`}
                  onDragStart={(e) => {
                    if (!phaseAllowsReorder) {
                      e.preventDefault()
                      return
                    }
                    e.dataTransfer.setData('application/x-sgez-section-index', String(index))
                    e.dataTransfer.effectAllowed = 'move'
                    setDraggingIndex(index)
                  }}
                  onDragEnd={() => {
                    setDraggingIndex(null)
                    setDragOverIndex(null)
                  }}
                  onKeyDown={(e) => {
                    if (!phaseAllowsReorder) return
                    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
                    e.preventDefault()
                    if (e.key === 'ArrowUp' && index > 0) {
                      setLandingSections(moveSection(landingSections, index, index - 1))
                    } else if (e.key === 'ArrowDown' && index < landingSections.length - 1) {
                      setLandingSections(moveSection(landingSections, index, index + 1))
                    }
                  }}
                >
                  <span className="text-[10px] tracking-[0.15em]">:::</span>
                  <span className="ml-1 tabular-nums">{index + 1}</span>
                </span>
                <button
                  type="button"
                  className={`min-w-0 rounded-lg px-1 py-0.5 text-left ${guidedFlow ? 'cursor-default' : 'hover:bg-slate-50/90'}`}
                  onClick={() => {
                    if (guidedFlow) return
                    toggleExpanded(section.id)
                  }}
                >
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    {sectionTypeLabel}
                  </span>
                  {activeTemplateLabel ? (
                    <span className="ml-2 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-brand-navy">
                      {activeTemplateLabel}
                    </span>
                  ) : null}
                  <span className="mt-1 block break-words text-base font-bold leading-snug text-slate-900">
                    {contentTitle || sectionTypeLabel}
                  </span>
                </button>
                <div className="col-span-2 flex min-w-0 flex-wrap items-center justify-start gap-1 pl-[3.25rem]">
                  {section.type === 'ad-review' ? (
                    <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-900/90" title="페이지 하단 고정 · 네비 미포함">
                      하단 고정
                    </span>
                  ) : phaseAllowsMenuToggle ? (
                    <label className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-600">
                      <input
                        type="checkbox"
                        checked={enabled}
                        title="메뉴로 표시"
                        aria-label={`${sectionTypeLabel} 메뉴로 표시`}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setLandingSections((prev) => prev.map((item, idx) => (idx === index ? { ...item, enabled: checked } : item)))
                        }}
                      />
                      <span>메뉴</span>
                    </label>
                  ) : (
                    <span className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-500" title="「섹션 구성」단계에서 바꿀 수 있습니다">
                      메뉴 {enabled ? 'ON' : 'OFF'}
                    </span>
                  )}
                  {phaseAllowsReorder ? (
                    <>
                      <button
                        type="button"
                        className={`rounded border px-2 py-1 text-[11px] ${index > 0 ? 'hover:bg-slate-100' : 'opacity-40 cursor-not-allowed'}`}
                        onClick={() => setLandingSections(moveSection(landingSections, index, index - 1))}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={`rounded border px-2 py-1 text-[11px] ${index < landingSections.length - 1 ? 'hover:bg-slate-100' : 'opacity-40 cursor-not-allowed'}`}
                        onClick={() => setLandingSections(moveSection(landingSections, index, index + 1))}
                        disabled={index === landingSections.length - 1}
                      >
                        ↓
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className={`rounded border px-2 py-1 text-[11px] ${required || !phaseAllowsSectionRemove ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-100 text-red-700 border-red-100'}`}
                    onClick={() => {
                      if (required || !phaseAllowsSectionRemove) return
                      setLandingSections(landingSections.filter((_, i) => i !== index))
                    }}
                    disabled={required || !phaseAllowsSectionRemove}
                  >
                    삭제
                  </button>
                  {!guidedFlow ? (
                    <button type="button" className="rounded border px-2 py-1 text-[11px] text-slate-600" onClick={() => toggleExpanded(section.id)}>
                      {expanded ? '접기' : '펼치기'}
                    </button>
                  ) : null}
                </div>
              </div>
              {showBodyInCard ? renderSectionBody(section, index) : null}
            </div>
          )
        })}
      </div>

      {availableSections.length > 0 && phaseAllowsAdd && (
        <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-white/60 p-3">
          <div className="text-[11px] font-semibold text-slate-500">섹션 추가</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {availableSections.map((item) => (
              <button
                key={item.type}
                type="button"
                className="rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800 hover:bg-teal-100"
                onClick={() => {
                  const draft = createLandingSectionDraft(item.type, landingSections)
                  if (!draft) return
                  setLandingSections([...landingSections, draft])
                  setExpandedSectionId(draft.id)
                }}
              >
                + {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
